"use client";
import { useMeasure } from "@/hooks/use-measure";
import { animate, motion, useMotionValue } from "framer-motion";
import { SpecularBentoCard } from "@/components/flagship/dom/SpecularBentoCard";
import { FlagshipSectionHeading } from "@/components/flagship/page/FlagshipSection";
import cn from "@/utils/class-names";
import { testimonials } from "@/data/testimonial-data";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useEffect, useState } from "react";




const TestimonialsColumn = (props: {
  className?: string;
  testimonials: typeof testimonials;
  duration?: number;
}) => {


  const FAST_DURATION = props.duration || 10;
  const SLOW_DURATION = 75;
  const [duration, setDuration] = useState(FAST_DURATION)

  let [ref, { height }] = useMeasure<HTMLDivElement>();
  const yTranslation = useMotionValue(0);
  const [mustFinish, setMustFinish] = useState(false);
  const [rerender, setRerender] = useState(false)
  // Fully PAUSED, not just slowed. The specular hover caches each card's rect
  // on pointer-enter to avoid a layout read per mousemove; a card still
  // creeping upward invalidates that cache immediately and the highlight
  // visibly lags behind the cursor. Stopping also lets people read the quote.
  const [paused, setPaused] = useState(false)


  useEffect(() => {
    if (paused) return;

    let controls;
    let finalPosition = -height / 2 - 12;

    if (mustFinish) {
      controls = animate(yTranslation, [yTranslation.get(), finalPosition], {

        ease: "linear",
        duration: duration * (1 - yTranslation.get() / finalPosition),
        onComplete: () => {
          setMustFinish(false);
          setRerender(!rerender);
        },
      })

    } else {
      controls = animate(yTranslation, [0, finalPosition], {
        ease: "linear",
        duration: duration,
        repeat: Infinity,
        repeatType: "loop",
        repeatDelay: 0,
      });

    }

    return controls.stop;
  }, [yTranslation, height, duration, rerender, paused]);

  const handleMouseEnter = () => setPaused(true);

  const handleMouseLeave = () => {
    // Resume from wherever the column stopped rather than snapping to 0.
    setMustFinish(true);
    setPaused(false);
  };

  return (
    (<div
      // Fixed column width: the cards no longer carry the legacy `.card`
      // sizing, so without this they stretch to fill the flex row and push the
      // columns past the container edge.
      className={cn('w-[min(340px,86vw)] shrink-0', props.className)}
    >
      <motion.div
        className="flex flex-col gap-6"
        style={{ y: yTranslation }}
        ref={ref}

        onTouchStart={handleMouseEnter}
        onTouchEnd={handleMouseLeave}
        onHoverStart={handleMouseEnter}
        onHoverEnd={handleMouseLeave}
      >
        {[...new Array(2)].fill(0).map((_, index) => (
          <div key={index} className="flex flex-col gap-6">
            {props.testimonials.map(({ text, imageSrc, name, username }) => (
              <SpecularBentoCard
                key={username}
                contentClassName="p-6"
                spotSize={200}
              >
                <p className="fg-ink-soft font-inter text-sm leading-relaxed">{text}</p>
                <div className="mt-5 flex items-center gap-3">
                  <Image
                    src={imageSrc}
                    alt={name}
                    width={40}
                    height={40}
                    className="h-10 w-10 shrink-0 rounded-full object-cover"
                  />
                  <div className="flex flex-col">
                    <span className="fg-ink font-lexend text-sm font-semibold leading-5">{name}</span>
                    <span className="fg-ink-muted font-inter text-xs leading-5">{username}</span>
                  </div>
                </div>
              </SpecularBentoCard>
            ))}
          </div>

        ))}
      </motion.div>
    </div>)
  );
};

const firstColumn = testimonials.slice(0, 3);
const secondColumn = testimonials.slice(3, 6);
const thirdColumn = testimonials.slice(6, 9);

export default function Testimonials({ variant = "primary" }: { variant?: string; }) {
  const t = useTranslations("Testimonial");
  return (
    <section id="testimonials" className="flagship">
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        viewport={{ once: true, amount: 0.2 }}
        className=""
      >
        {/* Same heading component `BentoSpecular` renders, so the two sections
            read as one surface. It stays an h2 — testimonials are a section, and
            every page that renders them (/, /about) already has its own h1.
            The `variant` prop no longer switches the heading; both pages get the
            flagship treatment. */}
        <FlagshipSectionHeading
          tagline={t("contentHeader.tagline")}
          title={t("contentHeader.title")}
          description={t("contentHeader.description")}
        />


        <motion.div
          initial={{ opacity: 0, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          className="container flex justify-center gap-6  md:mt-2 [mask-image:linear-gradient(to_bottom,transparent,black,black_75%,transparent)] max-h-[738px] overflow-hidden"
        >
          {
          }
          <TestimonialsColumn testimonials={firstColumn} duration={8} />
          <TestimonialsColumn testimonials={secondColumn} className="hidden md:block" duration={12} />
          <TestimonialsColumn testimonials={thirdColumn} className="hidden lg:block" duration={10} />
        </motion.div>
      </motion.div>
    </section >
  );
}

